import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import Logger from "../../services/Logger";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    APP_SITE_REGISTRY,
    CONTACT_TYPE,
    parseContactAppParam,
    parseContactTypeParam,
} from "../../config/appSiteLinks";
import { SURFACE, TEXT } from "../../dark-theme/palette";
import AuthService from "../../services/AuthService";
import { usePagesTranslation } from "../locales";
import { trackEvent } from "../../services/GoogleAnalytics";

export default function UserContactComplete() {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    let { fullName } = useParams();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const siteName = process.env.REACT_APP_SITE_NAME || "Puzzle Interact";

    const contactType = parseContactTypeParam(searchParams.get("type"));
    const contactApp = parseContactAppParam(searchParams.get("app"));
    const isFeedback = contactType === CONTACT_TYPE.FEEDBACK;
    const appName = useMemo(
        () => (contactApp && APP_SITE_REGISTRY[contactApp]
            ? APP_SITE_REGISTRY[contactApp].displayName
            : null),
        [contactApp]
    );

    useEffect(() => {
        trackEvent("entered", { name: "UserContactComplete" });
    }, []);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const authStatus = await AuthService.isAuthenticated();
                setIsAuthenticated(authStatus);
            } catch (error) {
                Logger.error('Error checking authentication:', error);
                setIsAuthenticated(false);
            }
        };
        checkAuth();
    }, []);

    const displayName = fullName ? decodeURIComponent(fullName) : t("profile.contact.complete.user");
    const copyVars = { siteName, appName: appName || siteName, fullName: displayName };

    const thankYouTitle = isFeedback
        ? t(
            appName
                ? "profile.contact.complete.thank-you-title-feedback-app"
                : "profile.contact.complete.thank-you-title-feedback",
            copyVars
        )
        : t("profile.contact.complete.thank-you-title", copyVars);
    const messageImportant = t(
        isFeedback
            ? "profile.contact.complete.message-important-feedback"
            : "profile.contact.complete.message-important",
        copyVars
    );
    const willRespond = t(
        isFeedback
            ? "profile.contact.complete.will-respond-feedback"
            : "profile.contact.complete.will-respond",
        copyVars
    );
    const hereToHelp = t(
        isFeedback
            ? "profile.contact.complete.here-to-help-feedback"
            : "profile.contact.complete.here-to-help",
        copyVars
    );

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            <Grid container spacing={4}>
                <Grid
                    size={{ xs: 12 }}
                    sx={{
                        color: TEXT.primary,
                        backgroundColor: SURFACE.card,
                        display: "flex",
                        justifyContent: "flex-start",
                        textAlign: "left",
                        p: 4,
                        borderRadius: 2,
                        boxShadow: 1,
                    }}
                >
                    <Box sx={{ width: "100%" }}>
                        <Typography
                            variant="h3"
                            sx={{
                                color: "#3BA666",
                                fontSize: { xs: "32px", md: "48px" },
                                fontWeight: 600,
                                mb: 4,
                            }}
                        >
                            {thankYouTitle}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: TEXT.primary,
                                fontSize: "18px",
                                lineHeight: 1.8,
                                mb: 2,
                            }}
                        >
                            {t("profile.contact.complete.dear", copyVars)}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: TEXT.primary,
                                fontSize: "18px",
                                lineHeight: 1.8,
                                mb: 2,
                            }}
                        >
                            {messageImportant}
                        </Typography>
                        {!isAuthenticated && !isFeedback && (
                            <Typography
                                variant="body1"
                                sx={{
                                    color: "#3BA666",
                                    fontSize: "18px",
                                    lineHeight: 1.8,
                                    mb: 2,
                                    fontWeight: 600,
                                }}
                            >
                                {t("profile.contact.complete.email-verify")}
                            </Typography>
                        )}
                        <Typography
                            variant="body1"
                            sx={{
                                color: TEXT.primary,
                                fontSize: "18px",
                                lineHeight: 1.8,
                                mb: 2,
                            }}
                        >
                            {willRespond}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: TEXT.primary,
                                fontSize: "18px",
                                lineHeight: 1.8,
                                mb: 2,
                            }}
                        >
                            {hereToHelp}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: "#3BA666",
                                fontSize: "18px",
                                fontWeight: 600,
                                mt: 3,
                                whiteSpace: "pre-line",
                            }}
                        >
                            {t("profile.contact.complete.regards", copyVars)}
                        </Typography>
                        <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
                            <Button
                                variant="contained"
                                onClick={() => navigate("/")}
                                sx={{
                                    backgroundColor: "#3BA666",
                                    color: "white",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    px: 4,
                                    py: 1.5,
                                    "&:hover": {
                                        backgroundColor: "#2e7d32",
                                    },
                                }}
                            >
                                {t("profile.contact.complete.home-btn")}
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}
